import { PropsMap, propsMapToArray } from 'utils/constants.helpers';
import { generateGuid } from 'utils/helpers';
import { FeesFormField, FeesFormFieldOption } from './fee-step.interface';
import { FeesStepInstance } from 'services/professional/step/step.interface';
import moment from 'moment-mini';
import _get from 'lodash-es/get';

export const FEES_FORM_FIELD_TYPE_MAP: PropsMap = {
  REGULAR_PRICE: { id: 1, name: 'Regular price' },
  DISCOUNTED_PRICE: { id: 2, name: 'Discount price' },
  PRICE_OPTIONS: { id: 3, name: 'Price options' },
};

export const FEES_FORM_FIELD_TYPE_ARRAY = propsMapToArray(FEES_FORM_FIELD_TYPE_MAP);

export enum FEES_FORM_FIELD_TYPE {
  REGULAR_PRICE = 1,
  DISCOUNTED_PRICE = 2,
  PRICE_OPTIONS = 3,
}

export function createDefaultFeeFormField(position: number): FeesFormField {
  return {
    typeId: FEES_FORM_FIELD_TYPE.REGULAR_PRICE,
    name: '',
    order: position + 1,
    fieldId: generateGuid(),
    options: [getFieldOption(FEES_FORM_FIELD_TYPE.REGULAR_PRICE)],
  };
}

export function getFieldOption(fieldTypeId: number): FeesFormFieldOption {
  return {
    name: '',
    price: null,
    optionId: generateGuid(),
    ...(fieldTypeId === FEES_FORM_FIELD_TYPE.DISCOUNTED_PRICE ? { discountRate: null } : {}),
  };
}

export function trimFeesFormFields(fields: FeesFormField[]) {
  return fields.map(field => {
    return {
      ...field,
      name: field.name.trim(),
      options: field.options.map(option => ({ ...option, name: option.name.trim() })),
    };
  });
}

export function roundNumberToFixDigits(value: number, digitsNumber = 2): number {
  return +value.toFixed(digitsNumber);
}

export function hasGracePeriodCome(stepEndAfter = '', gracePeriod = 0): boolean {
  const gracePeriodStart = moment(stepEndAfter).add(gracePeriod, 'day');
  return moment()
    .utc()
    .isSameOrAfter(gracePeriodStart, 'day');
}

export function calculateFeeFormFieldTotal(options: FeesFormFieldOption[], fieldTypeId: FEES_FORM_FIELD_TYPE): number {
  return roundNumberToFixDigits(
    options
      .map(option => calculateFieldSubTotal(option, fieldTypeId))
      .reduce((subTotalSum, subTotal) => subTotalSum + subTotal),
  );
}

export function calculateFeeFormGrandTotal(feesFormFields: FeesFormField[]): number {
  const formSum = feesFormFields
    .map(field => calculateFeeFormFieldTotal(field.options, field.typeId))
    .reduce((fieldSum, optionsSum) => fieldSum + optionsSum);
  return roundNumberToFixDigits(formSum);
}

export function calculateStepFee(feeData: {
  stepEndDate: string;
  isLateFeeEnabled: boolean;
  fee: number;
  gracePeriod: number;
  lateFee: number;
}): number {
  if (feeData.isLateFeeEnabled && hasGracePeriodCome(feeData.stepEndDate, feeData.gracePeriod)) {
    return feeData.fee + feeData.lateFee;
  } else {
    return feeData.fee;
  }
}

export function calculateFieldSubTotal(option: FeesFormFieldOption, fieldTypeId: FEES_FORM_FIELD_TYPE): number {
  const fullPrice = (option.quantity || 0) * (option.price || 0);
  const finalSubtotal =
    fieldTypeId === FEES_FORM_FIELD_TYPE.DISCOUNTED_PRICE
      ? getDiscountedPrice({ ...option, price: fullPrice })
      : fullPrice;
  return roundNumberToFixDigits(finalSubtotal);
}

export function getDiscountedPrice(option: FeesFormFieldOption): number {
  return roundNumberToFixDigits((1 - option.discountRate || 0) * option.price);
}

export function noFeeFormOptionsQuantity(feeForm: FeesFormField[]): boolean {
  return _get(feeForm, 'length') && !feeForm.some(field => field.options.some(option => !!option.quantity));
}

export function formShouldButIsNotFilled(feeForm: FeesFormField[], fee: number): boolean {
  return !fee && noFeeFormOptionsQuantity(feeForm);
}

export function getFullFeeStepAmount(step: FeesStepInstance): number {
  const { isLateFeeEnabled, fee, lateFee, gracePeriod, feesFormFields } = step.content;
  const stepFeeData = {
    stepEndDate: step.end_date,
    isLateFeeEnabled,
    fee,
    gracePeriod,
    lateFee,
  };
  const formSum = _get(feesFormFields, 'length') ? calculateFeeFormGrandTotal(feesFormFields) : 0;
  return roundNumberToFixDigits(calculateStepFee(stepFeeData) + formSum);
}
